import { QueryObserverResult } from '@tanstack/react-query';
import { MappedEntity, SnakeMeta, TSDatum } from '@toggle/toggle';
import { useMemo } from 'react';

import { TSMeta } from '~/api/timeseries/timeseries-schema';
import { IndexedTsPoint } from '~/components/snake-chart/snake-chart-utils';
import { priceSuffix } from '~/utils/currency/currency';

import { useFetchSnakeByName } from '../use-fetch-snake-by-name/useFetchSnakeByName';
import { useFetchSnakeMetaV2 } from '../use-fetch-snake-meta-v2/useFetchSnakeMetaV2';

const HORIZON_NUMBER_OF_DAYS = 180;
export const SPY_ARCA_ENTITY = 'spy_arca';
export const SPY_ARCA_SNAKE = 'spy_arca.price.ds';

interface UseSpyArcaProps {
  thresholdsDates?: Record<string, string>;
}

interface UseSpyArcaReturnProps {
  tsData: SpxTsData[];
  entity?: MappedEntity;
  priceSuffix: string;
  name?: string;
  snakeMeta?: SnakeMeta;
  refetch: () => Promise<
    QueryObserverResult<
      {
        tsData?: TSDatum[];
        meta?: TSMeta;
      },
      Error
    >
  >;
  loading: boolean;
}
export interface SpxTsData extends IndexedTsPoint {
  direction?: string;
}

export const useSpyArca = ({
  thresholdsDates,
}: UseSpyArcaProps): UseSpyArcaReturnProps => {
  const {
    data: snakeByName,
    refetch,
    isLoading,
  } = useFetchSnakeByName(SPY_ARCA_SNAKE, HORIZON_NUMBER_OF_DAYS);
  const { data: snakeMetaV2 } = useFetchSnakeMetaV2(SPY_ARCA_SNAKE);

  const suffix: string = useMemo(() => {
    return priceSuffix(
      snakeMetaV2?.meta?.display_format,
      snakeMetaV2?.meta?.currency
    );
  }, [snakeMetaV2?.meta]);

  const tsData: SpxTsData[] = useMemo(() => {
    if (!snakeByName?.tsData) {
      return [];
    }

    return snakeByName.tsData.map((p, i) => {
      return {
        index: i,
        value: p.value,
        date: new Date(p.index),
        direction: thresholdsDates?.[p.index],
      };
    });
  }, [snakeByName?.tsData, thresholdsDates]);

  return {
    tsData,
    priceSuffix: suffix,
    name: snakeByName?.meta?.name,
    snakeMeta: snakeMetaV2?.meta,
    refetch,
    loading: isLoading,
  };
};
